import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Breadcrumbs.module.scss';
import ChevronRightIcon from '../../../assets/iconsTsx/ChevronRightIcon.tsx';
import { routeConfig, RouteNode, getParentForRoute, getProfileBreadcrumbLabel } from '../../../routes/routeConfig.ts';
import {
  parseNavigationParams,
  buildProfileUrl,
  parseProfessionalsListNavigationParams,
  buildProfessionalsListUrl,
  QUERY_PARAM_TO_TAB,
  resolveProfileTab,
  getGestionSectionFromQuery,
} from '../../../utils/navigationHelpers.ts';

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const searchParams = new URLSearchParams(location.search);

  const findNodeForPath = (): RouteNode | null => {
    let best: RouteNode | null = null;
    for (const n of routeConfig) {
      const pattern = n.pattern.replace(/:\w+/g, '[^/]+');
      const re = new RegExp(`^${pattern}$`);
      if (re.test(path)) {
        if (!best || n.pattern.length > best.pattern.length) best = n;
      }
    }
    return best;
  };

  const inferLabelFromPath = (pathname: string): string => {
    const segs = pathname.split('/').filter(Boolean);
    const last = segs[segs.length - 1] || '';
    const decoded = decodeURIComponent(last);
    const alias: Record<string, string> = {
      crear: 'Crear',
      edit: 'Editar',
      editar: 'Editar',
      explorar: 'Explorar',
      'mercado-de-trabajo': 'Mercado de trabajo',
      marketplace: 'Marketplace'
    };
    if (alias[decoded]) return alias[decoded];
    if (/^\d+$/.test(decoded)) return 'Detalle';
    const pretty = decoded.replace(/[-_]+/g, ' ');
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
  };

  const matched = findNodeForPath();
  const chain: RouteNode[] = [];
  let node: RouteNode | null = matched;
  if (!node) {
    const dash = routeConfig.find(r => r.key === 'dashboard') || null;
    const navParams = parseNavigationParams(searchParams);
    const parent = navParams && navParams.from === 'perfil' ? 'perfil' : (dash ? 'dashboard' : undefined);
    const pseudo: RouteNode = {
      // @ts-ignore
      key: 'pseudo' as any,
      pattern: path,
      label: inferLabelFromPath(path),
      parent
    };
    if (parent === 'perfil' && navParams) {
      const perfilNode = routeConfig.find(r => r.key === 'perfil') || null;
      if (perfilNode) chain.push(perfilNode);
    } else if (dash) {
      chain.push(dash);
    }
    chain.push(pseudo);
  } else {
    let current = node;
    while (current) {
      chain.unshift(current);
      const parentKey = getParentForRoute(current, path, searchParams);
      if (!parentKey) break;
      
      if (parentKey === 'perfil') {
        const perfilNode = routeConfig.find(r => r.key === 'perfil') || null;
        if (perfilNode) {
          chain.unshift(perfilNode);
        }
        break;
      }
      
      current = routeConfig.find(r => r.key === parentKey) || null;
    }
  }

  const toUrl = (n: RouteNode, params?: Record<string, string>) => {
    let url = n.pattern.replace(/:\w+/g, '');
    if (params) {
      Object.keys(params).forEach(key => {
        url = url.replace(`:${key}`, params[key]);
      });
    }
    return url;
  };

  const currentNode = chain[chain.length - 1];
  const navParams = parseNavigationParams(searchParams);
  const professionalsNav = parseProfessionalsListNavigationParams(searchParams);
  const professionalsParentNode = professionalsNav
    ? routeConfig.find(r => r.key === 'usersList') || null
    : null;

  const handleProfileClick = (tabOverride?: string | null) => {
    if (navParams) {
      if (tabOverride === null) {
        navigate(
          buildProfileUrl(navParams.userId, navParams.userName, null)
        );
        return;
      }
      const tabKey = tabOverride ?? navParams.tab;
      const opts =
        tabKey === 'gestion' && navParams.section
          ? { section: navParams.section }
          : undefined;
      const profileUrl = buildProfileUrl(
        navParams.userId,
        navParams.userName,
        tabKey,
        opts
      );
      navigate(profileUrl);
    } else {
      const perfilNode = chain.find(n => n.key === 'perfil');
      if (perfilNode) {
        const segments = path.split('/').filter(Boolean);
        if (segments.length >= 3 && segments[0] === 'profesionales') {
          const userId = segments[1];
          const userName = decodeURIComponent(segments[2]);
          const tabParamRaw =
            typeof tabOverride === 'undefined'
              ? searchParams.get('tab')
              : tabOverride;
          const resolved = resolveProfileTab(tabParamRaw);
          const sec = getGestionSectionFromQuery(searchParams);
          const opts =
            resolved === 'gestion' && sec ? { section: sec } : undefined;
          const profileUrl = buildProfileUrl(
            userId,
            userName,
            tabParamRaw ?? null,
            opts
          );
          navigate(profileUrl);
        }
      }
    }
  };

  const handleParentClick = (parentNode: RouteNode, params?: Record<string, string>) => {
    if (professionalsNav && parentNode.key === 'usersList') {
      const url = buildProfessionalsListUrl({
        skillId: professionalsNav.skillId,
        jurisdictionIds: professionalsNav.jurisdictionIds,
        allCountry: professionalsNav.allCountry,
        page: professionalsNav.page,
      });
      navigate(url);
      return;
    }
    if (parentNode.key === 'perfil' && navParams) {
      handleProfileClick();
    } else {
      navigate(toUrl(parentNode, params));
    }
  };

  let parentNode: RouteNode | null = null;
  if (professionalsParentNode) {
    parentNode = professionalsParentNode;
  } else if (chain.length > 1) {
    parentNode = chain[chain.length - 2];
  }

  const getParentLabel = (node: RouteNode | null): string | null => {
    if (!node) return null;
    if (professionalsNav && node.key === 'usersList') {
      return typeof node.label === 'string' ? node.label : node.label({});
    }
    if (node.key === 'perfil') {
      return getProfileBreadcrumbLabel(searchParams);
    }
    return typeof node.label === 'string' ? node.label : node.label({});
  };

  const parentLabel = getParentLabel(parentNode);
  const shouldShowParent = Boolean(parentNode && parentLabel);

  const showProfileBreadcrumbs = Boolean(
    parentNode &&
      parentNode.key === 'perfil' &&
      navParams &&
      navParams.from === 'perfil'
  );

  const profileTabLabel =
    showProfileBreadcrumbs && navParams?.tab
      ? QUERY_PARAM_TO_TAB[navParams.tab] || null
      : null;

  return (
    <div className={styles.container}>
      {showProfileBreadcrumbs && (
        <>
          <span className={styles.item}>
            <button
              type="button"
              className={styles.link}
              onClick={() => {
                handleProfileClick(null);
              }}
            >
              Perfil
            </button>
            {(profileTabLabel || currentNode) && (
              <span className={styles.sep}><ChevronRightIcon /></span>
            )}
          </span>
          {profileTabLabel && (
            <span className={styles.item}>
              <button
                type="button"
                className={styles.link}
                onClick={() => {
                  handleProfileClick();
                }}
              >
                {profileTabLabel}
              </button>
              {currentNode && (
                <span className={styles.sep}><ChevronRightIcon /></span>
              )}
            </span>
          )}
        </>
      )}
      {!showProfileBreadcrumbs && shouldShowParent && (
        <span className={styles.item}>
          <button
            type="button"
            className={styles.link}
            onClick={() => {
              handleParentClick(parentNode);
            }}
          >
            {parentLabel}
          </button>
          {currentNode && <span className={styles.sep}><ChevronRightIcon /></span>}
        </span>
      )}
      {currentNode && (
        <span className={styles.item}>
          <span className={styles.current}>{typeof currentNode.label === 'string' ? currentNode.label : currentNode.label({})}</span>
        </span>
      )}
    </div>
  );
};

export default Breadcrumbs;


